import type { Router } from 'vue-router';
import { onSignalrConnected, onSignalrReconnected, signalrInvoke } from '@lib/signalr';
import { hasPendingWrites } from '@lib/pending-writes';

// A tab that loaded before a deploy keeps running the old bundle indefinitely — nothing
// forces it to fetch the new index.html, and preloadRouteChunks() has already warmed every
// lazy chunk, so the router.onError reload in entry-client.ts never fires either. When the
// deploy changes an API contract, those tabs crash.
//
// Every deploy restarts the backend, which drops all SignalR connections, so the reconnect
// is the deploy signal — and it reaches exactly the parked tabs that never navigate. We
// compare the server's release tag against the one it reported when *this page* connected,
// rather than against our own VITE_RELEASE_TAG: it needs no agreement between the two build
// pipelines, and a freshly loaded page can never look stale, so a tag mismatch during a
// half-finished deploy can't turn into a reload loop.

let bootReleaseTag: string | null = null;
let stale = false;

async function fetchServerReleaseTag(): Promise<string | null> {
  try {
    const tag = await signalrInvoke<string>('GetReleaseTag');
    return tag || null; // empty on unstamped builds (local dev) — nothing to compare
  } catch {
    return null;
  }
}

/** How long to wait before asking again, when a write is holding the reload off. */
const PENDING_WRITE_RECHECK_MS = 5000;

/**
 * Reload in the background as soon as the tab is hidden, and never while it is visible.
 *
 * A hidden tab reloads with no interaction behind it, so the browser's own unload prompt is
 * suppressed — this is the one path where an unconfirmed write would disappear without the
 * user ever being asked. Wait it out instead, and keep asking, since a hidden tab never
 * hears about the write settling.
 */
function reloadWhenHidden() {
  const reloadIfSafe = () => {
    if (!document.hidden) return;
    if (hasPendingWrites()) {
      setTimeout(reloadIfSafe, PENDING_WRITE_RECHECK_MS);
      return;
    }

    document.removeEventListener('visibilitychange', reloadIfSafe);
    window.location.reload();
  };

  document.addEventListener('visibilitychange', reloadIfSafe);
  reloadIfSafe();
}

/**
 * Watch for the backend being redeployed under this tab, and pick up the new bundle without
 * ever interrupting the user: reload while the tab is hidden, otherwise turn the next route
 * navigation into a full page load.
 */
export function watchForNewRelease(router: Router) {
  onSignalrConnected(async () => {
    bootReleaseTag ??= await fetchServerReleaseTag();
  });

  onSignalrReconnected(async () => {
    if (stale) return;
    const tag = await fetchServerReleaseTag();
    if (!tag) return;
    if (!bootReleaseTag) {
      // The pull at connect failed — take this as the baseline instead of giving up on
      // staleness detection for the rest of the tab's life.
      bootReleaseTag = tag;
      return;
    }
    if (tag === bootReleaseTag) return;

    stale = true;
    reloadWhenHidden();
  });

  router.beforeEach((to, from) => {
    // Query-only changes (filter/season writes to the URL) aren't navigations to the user —
    // reloading on those would interrupt exactly what they're doing.
    if (!stale || to.path === from.path) return;
    // Full page load rather than router navigation, so the new index.html is fetched.
    window.location.href = to.fullPath;
    return false;
  });
}
